/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Settings > Real Estate > Customer portal (module RE_CUSTOMER_PORTAL): the
 * documents each kind of application asks for. GET/PUT /re/settings/portal;
 * saving needs a system admin (the backend checks too), everyone else with
 * the applications grant sees the lists read-only. Whether the portal is on
 * is the provider's switch in the admin portal, so it is shown, not edited.
 */

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Plus, RotateCcw, Trash2 } from "lucide-react";

import { useAuth } from "auth/Auth";
import { v2 as t } from "styles/tokens/v2";
import { getApiErrorMessage } from "utils/apiErrors";
import { checkChecklist, checklistKey } from "../v2/applications/logic";

/** New items take their key from the name typed; saved items keep theirs (uploads refer to it). */
export function withKeys(items) {
  const taken = items.filter((i) => !i.isNew).map((i) => i.key);
  return items.map((i) => {
    if (!i.isNew) return i;
    const key = checklistKey(i.label, taken);
    taken.push(key);
    return { ...i, key, isNew: false };
  });
}

const TYPES = [
  { code: "RENTAL", label: "Rental applications" },
  { code: "PURCHASE", label: "Offers and purchase applications" },
];

function ChecklistEditor({ type, items, defaults, readOnly, onChange }) {
  const update = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <Box sx={{ mb: 3 }}>
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: t.text, mb: 1 }}>{type.label}</Typography>
      {items.length === 0 && <Typography sx={{ fontSize: 14, color: t.text3, mb: 1 }}>No documents asked for.</Typography>}
      <Box sx={{ display: "grid", gap: 1 }}>
        {items.map((item, i) => (
          <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              size="small"
              label="Document"
              value={item.label}
              onChange={(e) => update(i, { label: e.target.value })}
              InputProps={{ readOnly }}
              inputProps={{ maxLength: 200 }}
              sx={{ flex: "1 1 280px" }}
            />
            <FormControlLabel
              control={<Checkbox checked={Boolean(item.required)} disabled={readOnly} onChange={(e) => update(i, { required: e.target.checked })} />}
              label="Required"
            />
            {!readOnly && (
              <IconButton aria-label={`Remove ${item.label || "document"}`} onClick={() => onChange(items.filter((_, j) => j !== i))}>
                <Trash2 size={18} />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>
      {!readOnly && (
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button
            size="small"
            startIcon={<Plus size={16} />}
            disabled={items.length >= 20}
            onClick={() => onChange([...items, { key: checklistKey(`new ${items.length + 1}`, items.map((x) => x.key)), label: "", required: true, isNew: true }])}
          >
            Add document
          </Button>
          <Button size="small" startIcon={<RotateCcw size={16} />} onClick={() => onChange(defaults.map((d) => ({ ...d })))}>
            Use the standard list
          </Button>
        </Box>
      )}
    </Box>
  );
}

ChecklistEditor.propTypes = {
  type: PropTypes.shape({ code: PropTypes.string, label: PropTypes.string }).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaults: PropTypes.arrayOf(PropTypes.object).isRequired,
  readOnly: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function PortalSettingsCard({ isSystemAdmin, onSaved }) {
  const { axiosInstance } = useAuth();
  const [data, setData] = useState(null);
  const [lists, setLists] = useState({ RENTAL: [], PURCHASE: [] });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const apply = (body) => {
    setData(body);
    setLists({ RENTAL: body.checklists.RENTAL.map((x) => ({ ...x })), PURCHASE: body.checklists.PURCHASE.map((x) => ({ ...x })) });
    setDirty(false);
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: body } = await axiosInstance.get("/re/settings/portal");
      apply(body);
    } catch (err) {
      setError(getApiErrorMessage(err, "The customer portal settings could not be loaded."));
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const rental = checkChecklist(withKeys(lists.RENTAL));
    const purchase = checkChecklist(withKeys(lists.PURCHASE));
    const problem = rental.error || purchase.error;
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data: body } = await axiosInstance.put("/re/settings/portal", { RENTAL: rental.items, PURCHASE: purchase.items });
      apply(body);
      onSaved?.("Customer portal documents saved");
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save the document lists."));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return error ? <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert> : <CircularProgress size={22} />;
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: t.text2, mb: 2 }}>
        {isSystemAdmin
          ? "Applicants see these on their application and upload each one from their account. Changes apply to new and open applications."
          : "Applicants see these on their application. Ask an administrator to change them."}
      </Typography>
      {TYPES.map((type) => (
        <ChecklistEditor
          key={type.code}
          type={type}
          items={lists[type.code]}
          defaults={data.defaults[type.code] || []}
          readOnly={!isSystemAdmin}
          onChange={(next) => { setLists((cur) => ({ ...cur, [type.code]: next })); setDirty(true); }}
        />
      ))}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {isSystemAdmin && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={save} disabled={!dirty || saving} sx={{ color: "#FFFFFF !important" }}>
            {saving ? "Saving…" : "Save documents"}
          </Button>
          <Button onClick={() => apply(data)} disabled={!dirty || saving}>Discard changes</Button>
        </Box>
      )}
    </Box>
  );
}

PortalSettingsCard.propTypes = { isSystemAdmin: PropTypes.bool, onSaved: PropTypes.func };
PortalSettingsCard.defaultProps = { isSystemAdmin: false, onSaved: null };
